import React, { useEffect, useState } from "react";
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { Smartphone, Monitor, Tablet, Globe, Eye, MousePointerClick } from "lucide-react";
import { api } from "../../lib/api";

const COLORS = ["#C5A059", "#1A1A1A", "#2D6A4F", "#5C5A56", "#B38F4D", "#C92A2A", "#2C2A26"];

const DeviceIcon = ({ name }) => {
    if (name === "mobile") return <Smartphone className="w-4 h-4" />;
    if (name === "tablet") return <Tablet className="w-4 h-4" />;
    if (name === "desktop") return <Monitor className="w-4 h-4" />;
    return <Globe className="w-4 h-4" />;
};

const Card = ({ title, children, className = "" }) => (
    <div className={`bg-white border border-brand-border rounded-sm p-6 ${className}`}>
        <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-brand-ink mb-4">{title}</h3>
        <div className="min-h-[12rem]">{children}</div>
    </div>
);

const Metrics = () => {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(30);

    useEffect(() => {
        api.get(`/admin/metrics?days=${days}`).then(({ data }) => setData(data));
    }, [days]);

    if (!data) return <div className="text-brand-inkSoft" data-testid="metrics-loading">Carregando…</div>;

    const totalEvents = (data.types.page_view || 0) + (data.types.product_view || 0) + (data.types.ml_click || 0);

    return (
        <div data-testid="admin-metrics-page" className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-brand-gold mb-1">Analytics</div>
                    <h1 className="font-heading font-bold text-3xl text-brand-ink">Métricas</h1>
                    <p className="text-sm text-brand-inkSoft mt-1">Comportamento dos visitantes e desempenho do catálogo.</p>
                </div>
                <select value={days} onChange={(e) => setDays(parseInt(e.target.value))} data-testid="metrics-range" className="border border-brand-border px-4 py-2.5 text-sm rounded-sm bg-white">
                    <option value={7}>Últimos 7 dias</option>
                    <option value={30}>Últimos 30 dias</option>
                    <option value={90}>Últimos 90 dias</option>
                    <option value={365}>Último ano</option>
                </select>
            </div>

            {/* Resumo */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white border border-brand-border p-5 rounded-sm">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-gold">Total de eventos</div>
                    <div className="font-heading font-bold text-3xl text-brand-ink mt-2">{totalEvents}</div>
                </div>
                <div className="bg-white border border-brand-border p-5 rounded-sm">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-gold">Visitas à home</div>
                    <div className="font-heading font-bold text-3xl text-brand-ink mt-2">{data.types.page_view || 0}</div>
                </div>
                <div className="bg-white border border-brand-border p-5 rounded-sm">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-gold">Visualizações de produto</div>
                    <div className="font-heading font-bold text-3xl text-brand-ink mt-2">{data.types.product_view || 0}</div>
                </div>
                <div className="bg-brand-ink text-white p-5 rounded-sm">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-gold">Cliques Mercado Livre</div>
                    <div className="font-heading font-bold text-3xl mt-2">{data.types.ml_click || 0}</div>
                </div>
            </div>

            {/* Timeline */}
            <Card title="Evolução diária">
                {data.timeline.length === 0 ? (
                    <div className="text-sm text-brand-inkSoft py-8 text-center">Sem dados ainda no período selecionado.</div>
                ) : (
                    <div className="h-72" data-testid="metrics-timeline">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={data.timeline}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Line type="monotone" dataKey="page_view" name="Visitas" stroke="#1A1A1A" strokeWidth={2} />
                                <Line type="monotone" dataKey="product_view" name="Visualizações" stroke="#5C5A56" strokeWidth={2} />
                                <Line type="monotone" dataKey="ml_click" name="Cliques ML" stroke="#C5A059" strokeWidth={2} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Devices */}
                <Card title="Dispositivos">
                    {data.devices.length === 0 ? (
                        <div className="text-sm text-brand-inkSoft py-8 text-center">Sem dados.</div>
                    ) : (
                        <div className="grid grid-cols-2 gap-4 items-center">
                            <div className="h-56" data-testid="metrics-devices">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={data.devices} dataKey="count" nameKey="device" innerRadius={40} outerRadius={75}>
                                            {data.devices.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <ul className="space-y-2">
                                {data.devices.map((d, i) => (
                                    <li key={d.device} className="flex items-center gap-2 text-sm">
                                        <span className="w-3 h-3 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
                                        <DeviceIcon name={d.device} />
                                        <span className="flex-1 capitalize">{d.device}</span>
                                        <span className="font-bold">{d.count}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </Card>

                {/* Sources */}
                <Card title="Origem dos acessos">
                    {data.sources.length === 0 ? (
                        <div className="text-sm text-brand-inkSoft py-8 text-center">Sem dados.</div>
                    ) : (
                        <div className="h-56" data-testid="metrics-sources">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data.sources} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                                    <XAxis type="number" tick={{ fontSize: 11 }} />
                                    <YAxis dataKey="source" type="category" tick={{ fontSize: 11 }} width={90} />
                                    <Tooltip />
                                    <Bar dataKey="count" fill="#C5A059" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </Card>

                {/* Platforms */}
                <Card title="Cliques por plataforma">
                    {!data.platforms || data.platforms.length === 0 ? (
                        <div className="text-sm text-brand-inkSoft py-8 text-center">Sem cliques de compra ainda. Os números aparecem quando clientes clicarem em "Comprar".</div>
                    ) : (
                        <div className="grid grid-cols-2 gap-4 items-center">
                            <div className="h-56" data-testid="metrics-platforms">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={data.platforms} dataKey="count" nameKey="platform" innerRadius={40} outerRadius={75}>
                                            {data.platforms.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <ul className="space-y-2">
                                {data.platforms.map((p, i) => (
                                    <li key={p.platform} className="flex items-center gap-2 text-sm">
                                        <span className="w-3 h-3 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
                                        <span className="flex-1">{p.platform}</span>
                                        <span className="font-bold">{p.count}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </Card>

                {/* Top products */}
                <Card title="Produtos mais clicados">
                    {data.top_products.length === 0 ? (
                        <div className="text-sm text-brand-inkSoft py-8 text-center">Sem cliques ainda. Os números aparecem quando clientes clicarem em "Comprar agora".</div>
                    ) : (
                        <ul className="divide-y divide-brand-border" data-testid="metrics-top-products">
                            {data.top_products.map((p, i) => (
                                <li key={p.id} className="py-2.5 flex items-center gap-3 text-sm">
                                    <span className="w-6 text-xs font-bold text-brand-inkSoft">{i + 1}.</span>
                                    <span className="flex-1 font-medium text-brand-ink truncate">{p.name}</span>
                                    <span className="inline-flex items-center gap-1 text-brand-inkSoft text-xs"><Eye className="w-3 h-3" /> {p.view_count}</span>
                                    <span className="inline-flex items-center gap-1 font-bold text-brand-gold"><MousePointerClick className="w-3 h-3" /> {p.click_count}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>

                {/* Top categories */}
                <Card title="Categorias mais acessadas">
                    {data.top_categories.length === 0 ? (
                        <div className="text-sm text-brand-inkSoft py-8 text-center">Sem dados ainda.</div>
                    ) : (
                        <div className="h-56" data-testid="metrics-top-categories">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data.top_categories}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                                    <XAxis dataKey="category" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip />
                                    <Bar dataKey="count" fill="#1A1A1A" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default Metrics;
